import type { TranslateService } from '@ngx-translate/core';
import type { WebWidgetModel, WidgetMetaData } from '@web/types';
import type { FormWidgetData } from '@web/widgets/form/type/form-widget.type';

import { Widgets } from '@common/enums';
import { defaultWidgetParams, defaultWidgetPermission } from '@common/constants';
import { buttonIconTemplates } from '@common/icons/button-icons';

import { createFormWidget } from '@sites/entity-core';

import { FormViewComponent } from '@web/widgets/form/components/form-view/form-view.component';
import { FormControlComponent } from '@web/widgets/form/components/form-control/form-control.component';

// DEFAULT COMPONENT SETTINGS — sourced from the canonical entity factory in
// @sites/entity-core. The package owns the data shape; host-only concerns —
// the i18n strings and the default button-icon SVG — are passed in via
// `CreateLegacyFormWidgetOptions`, mirroring the button widget's
// `buttonOverrides` convention.
//
// `data` is a function so `WebWidgetsFactoryService.prepareWidgetData` invokes
// it with `meta` — a form dropped into a zero block starts from the zero
// variant's defaults (`type: 'zero'` envelope tag, extended-position grid).
const formData = (translateService: TranslateService, meta?: WidgetMetaData): FormWidgetData => {
    const options = {
        texts: {
            dealName: translateService.instant('crm_deal_name'),
            buttonLabel: translateService.instant('form_button_text'),
            modalButtonLabel: translateService.instant('confirm'),
            subscriptionMessageHtml: `<h3>${translateService.instant('form_default_notification')}</h3>`,
            modalTitle: translateService.instant('modal_form_title_text'),
            leadSource: translateService.instant('control_form_lead_sourse'),
        },
        buttonIcon: buttonIconTemplates.telegram,
    };

    // The overloads on `createFormWidget` are picked per literal arg —
    // calling each branch separately keeps overload resolution happy.
    const entity = meta?.type === 'zero' ? createFormWidget('zero', options) : createFormWidget('default', options);

    return entity.data as unknown as FormWidgetData;
};
// END DEFAULT SETTINGS

export const formWidgetModel: WebWidgetModel = {
    componentView: FormViewComponent,
    componentControl: FormControlComponent,
    name: Widgets.FORM,
    label: 'widget_form_label',
    data: formData,
    permission: defaultWidgetPermission,
    // params: defaultWidgetParams,
    params: { ...defaultWidgetParams, type: ['default', 'zero'] },
    // Control + view components own the form via `WebLegacyWidgetSignalFormService`;
    // the resolver takes the minimal load path (just `widget` / `isSpecific` /
    // `isEduSite` — no `widget$`, no `container$`, no `controlForm`).
    signalForm: true,
};
